import { useField, useFormikContext } from 'formik';
import { motion } from 'framer-motion';
import moment from 'moment';
import { LegacyRef, useEffect, useState } from 'react';
import { Calendar } from 'react-date-range';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // t
import { FaCalendarAlt } from 'react-icons/fa';
import useUserClickObserver from '../../../hooks/useUserClickObserver';

interface IAcademiaDatePicker {
  placeholder: string;
  label: string;
  name: string;
  alterDateFieldName?: string;
  defaultDate?: Date;
  noInitialDefaultDate?: boolean;
  setDateOnChange?: (date: string) => void;
  isDisabled?: boolean;
  isRequired?: boolean;
  [key: string]: any;
}

const AcademiaDatePicker = ({
  name,
  alterDateFieldName,
  label,
  placeholder,
  defaultDate,
  noInitialDefaultDate,
  setDateOnChange,
  isDisabled,
  isRequired,
  ...props
}: IAcademiaDatePicker) => {
  // integration of react hooks here
  const [date, setDate] = useState<Date | null>(
    noInitialDefaultDate ? null : defaultDate || new Date()
  );
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);

  // integration of formik hooks here
  const [field, meta] = useField(name);
  const { setFieldValue } = useFormikContext();

  // integration of custom hooks here
  const selectRef = useUserClickObserver(setShowDatePicker);

  // setting selected date into formik value here
  useEffect(() => {
    if (date) {
      setFieldValue(name, moment(date).format('DD-MM-YYYY'));
      setDateOnChange && setDateOnChange(moment(date).format('DD-MM-YYYY'));

      if (alterDateFieldName) {
        setFieldValue(alterDateFieldName, moment(date).format('DD-MM-YYYY'));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, name, setFieldValue, alterDateFieldName]);

  // rendering custom date picker component here
  return (
    <div>
      <label
        htmlFor={`input-${name}`}
        title={isRequired ? 'This field is required' : ''}
        className='block mb-1 text-legal-dark-blue font-medium text-base'
      >
        {label} {isRequired && <span className='text-base-red'>*</span>}
      </label>

      <motion.div
        initial={{ x: 0 }}
        animate={
          meta.touched && meta.error
            ? {
                x: [0, -10, 0, 10, 0],
                transition: {
                  duration: 0.1,
                  repeat: 2,
                },
              }
            : { x: 0 }
        }
        ref={selectRef as LegacyRef<HTMLDivElement>}
        className='relative cursor-pointer'
      >
        <div
          onClick={() => setShowDatePicker((state) => !state)}
          className='relative'
        >
          <input
            id={`input-${name}`}
            type='text'
            className={`border-2 outline-none rounded-md w-full p-3 text-legal-dark-blue font-semibold duration-300 text-base disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer active:scale-[0.98] ${
              meta.touched && meta.error
                ? 'border-base-red'
                : 'border-legal-dark-blue'
            }`}
            value={field.value}
            onChange={field.onChange}
            placeholder={placeholder}
            disabled={isDisabled}
            autoComplete='off'
            readOnly
          />

          <div className='absolute top-1/2 -translate-y-1/2 right-5'>
            <FaCalendarAlt className='size-5 text-legal-dark-blue' />
          </div>
        </div>

        <motion.div
          initial={{ height: 0 }}
          animate={showDatePicker ? { height: 'fit-content' } : { height: 0 }}
          className='absolute bg-base-white shadow-academia-sm rounded-md mt-1 overflow-hidden z-50'
        >
          <Calendar
            date={
              field.value
                ? new Date(
                    moment(field.value, 'DD-MM-YYYY').format('YYYY-MM-DD')
                  )
                : defaultDate
            }
            color='#35476F'
            onChange={(date) => {
              setDate(date);
              setShowDatePicker(false);
            }}
            {...props}
          />
        </motion.div>
      </motion.div>

      <motion.label
        initial={{ y: -20, opacity: 0, display: 'none' }}
        animate={
          meta.touched && meta.error
            ? { y: 0, opacity: 1, display: 'block' }
            : { y: -20, opacity: 0, transitionEnd: { display: 'none' } }
        }
        htmlFor={`input-${name}`}
        className='block mt-1 text-base-red text-base'
      >
        {meta.error}
      </motion.label>
    </div>
  );
};

export default AcademiaDatePicker;
